import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import { SavedViewChips } from "@/components/table/SavedViewChips";
import type { DataTableColumn, DataTableFilterConfig } from "@/components/table/columns";
import type { InlineEditCommitArgs } from "@/components/table/use-inline-edits";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type {
  DocumentListItemResource,
  DocumentReplacementResource,
  DocumentResource,
  Page,
  PropertyListItemResource,
  ReferenceListItemResource,
} from "@/lib/api-types";
import { useIsReadOnly } from "@/lib/current-actor";
import { labelFor } from "@/lib/field-labels";
import { formatDateCell } from "@/lib/format";
import type { EditCommitOutcome, EditCommitPayload } from "@/lib/inline-edit";
import {
  appendUnknownParams,
  buildListSearch,
  PAGE_PARAM,
  pageSizeFor,
  parseFilterParams,
  parsePageParam,
  parseSortParam,
  SORT_PARAM,
} from "@/lib/list-query";
import { showToast } from "@/lib/toast-queue";
import { useIsPhone } from "@/lib/use-media-query";

import { ReplaceDocumentDialog } from "./ReplaceDocumentDialog";

/**
 * `/documents` (Story 7.2) — this codebase's first-ever Documents
 * destination: the shared `DataTable` (Epic 2), real AD-14 sort/filter/
 * paging against `GET /documents`, real saved-views support
 * (`SavedViewChips`), and `filename`'s own narrow substring search
 * (judgement call 4 — a plain AD-14 query parameter, never a preview of
 * Epic 9's global search, `epics.md`'s own Story 9.1).
 *
 * `category`/`property_id` are `DataTable`'s own native Select-based
 * filters (`filters` prop, populated from `GET /reference-list-items?
 * list_key=document_category` and `GET /properties`). `filename` is *not*
 * — it is a free-text search, not a fixed-option dropdown, so it is a
 * plain `Input` managed locally, mirroring `LeasesList.tsx`'s own
 * `in_force` precedent: recognised in `FILTERABLE_FIELDS` for URL
 * parsing/round-tripping, but rendered outside `DataTable`'s own filter row.
 *
 * No "Add document" affordance anywhere on this page (judgement call 2):
 * attachment happens from an entity's own record page (`PropertyRecord.
 * tsx`'s own "Add document" dialog), never from this generic, unscoped
 * list — the AC's own fourth bullet names the record page, not this one,
 * as where upload/attach happens.
 *
 * No document record page exists (`app/domain/documents/__init__.py`'s own
 * doc comment) — the identifier column self-links back to this same list
 * (mirrors `AlertsList.tsx`'s/`BackupStatus.tsx`'s own identical "no record
 * page" `getRowHref` convention exactly); the real, useful per-row action is
 * a genuine "Download" link straight to the authenticated streaming route
 * (`GET /documents/{id}`), rendered via `DataTable`'s own `rowActions` slot
 * as a plain anchor — deliberately not a `Link`, since `react-router`'s
 * `Link` only ever performs client-side routing and this route is a real,
 * non-JSON, non-SPA download.
 */

const CATEGORY_FILTER_FIELD = "category";
const PROPERTY_FILTER_FIELD = "property_id";
const FILENAME_FILTER_FIELD = "filename";
const FILTERABLE_FIELDS = [CATEGORY_FILTER_FIELD, PROPERTY_FILTER_FIELD, FILENAME_FILTER_FIELD] as const;

const DOCUMENTS_LIST_KEY = "documents";

/** `1234567` -> "1.18 MB" — mirrors `BackupStatus.tsx`'s own identical,
 * deliberately local helper (no shared byte-formatter exists yet). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

/** `PATCH /documents/{id}`'s own body (Story 7.3, this codebase's first
 * generic document edit route) — one field, mirroring `TenantsList.tsx`'s
 * own `patchBodyFor`. Blank means "clear it" (`null`), not "leave it". */
function expirationDatePatchBody(payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error('unexpected transition-kind payload for column "expiration_date"');
  }
  return { expiration_date: payload.value === "" ? null : payload.value };
}

export function DocumentsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  const isReadOnly = useIsReadOnly();
  //: Story 15.2's own "Replace" action — mirrors `UsersList.tsx`'s own
  //: `removeTarget`/`ConfirmDialog` shape: the target row a per-row action
  //: is currently acting on, driving a single shared dialog instance rather
  //: than one dialog per row.
  const [replaceTarget, setReplaceTarget] = useState<DocumentListItemResource | null>(null);

  useEffect(() => {
    document.title = "Documents — PropDesk";
  }, []);

  const categoriesQuery = useQuery({
    queryKey: ["reference-list-items", "document_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=document_category"),
  });
  const propertiesQuery = useQuery({
    queryKey: ["properties", "list", "for-document-filter"],
    // `GET /properties` offers no `sort` parameter at all (`PropertyRecord.
    // tsx`'s own sibling, `PropertiesList.tsx`'s doc comment: "no sort/
    // filter beyond the one archive toggle") — this fetch asks for none,
    // just the largest legal `page_size` (`MAX_PAGE_SIZE`) so the filter
    // dropdown below lists every property, not only the first 50.
    queryFn: () => apiFetch<Page<PropertyListItemResource>>("/properties?page_size=200"),
  });

  const categoryLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of categoriesQuery.data?.data ?? []) {
      map[item.value] = item.label;
    }
    return map;
  }, [categoriesQuery.data]);

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const activeFilters = parseFilterParams(searchParams, FILTERABLE_FIELDS);
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const pageSize = pageSizeFor(isPhone);
  const search = appendUnknownParams(
    buildListSearch({ sort, filters: activeFilters, page, pageSize }),
    searchParams,
    FILTERABLE_FIELDS,
  );
  const listQueryKey = ["documents", "list", search] as const;

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<DocumentListItemResource>>(`/documents?${search}`),
    placeholderData: keepPreviousData,
  });

  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const unfilteredTotalQuery = useQuery({
    queryKey: ["documents", "total"],
    queryFn: () => apiFetch<Page<DocumentListItemResource>>("/documents?page_size=1"),
    enabled: hasActiveFilters,
  });

  const filenameTerm = searchParams.get(FILENAME_FILTER_FIELD) ?? "";

  function setFilenameTerm(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === "") {
      next.delete(FILENAME_FILTER_FIELD);
    } else {
      next.set(FILENAME_FILTER_FIELD, value);
    }
    next.delete(PAGE_PARAM);
    setSearchParams(next, { replace: true });
  }

  const filters: DataTableFilterConfig[] = [
    {
      field: CATEGORY_FILTER_FIELD,
      label: labelFor("category"),
      options: (categoriesQuery.data?.data ?? []).map((item) => ({ value: item.value, label: item.label })),
    },
    {
      field: PROPERTY_FILTER_FIELD,
      label: "Property",
      options: (propertiesQuery.data?.data ?? []).map((item) => ({ value: item.id, label: item.nickname })),
    },
  ];

  /** Writes only `expiration_date` into whichever cached page(s) hold this
   * row — `PATCH /documents/{id}` returns a `DocumentResource` (Story 7.1's
   * own frozen upload-response shape), not a `DocumentListItemResource`
   * (`entity_label`/`entity_url`/`uploaded_at` are this list's own added
   * fields, Story 7.2) — so this merges the one real field that changed
   * into the existing cached row rather than replacing it wholesale, which
   * would silently blank those extra fields. */
  function writeExpirationDateToCache(documentId: string, expirationDate: string | null) {
    queryClient.setQueriesData<Page<DocumentListItemResource>>({ queryKey: ["documents"] }, (existing) => {
      if (existing === undefined) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.map((candidate) =>
          candidate.id === documentId ? { ...candidate, expiration_date: expirationDate } : candidate,
        ),
      };
    });
  }

  async function commitExpirationDateEdit(
    args: InlineEditCommitArgs<DocumentListItemResource>,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<DocumentResource>(`/documents/${args.row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(expirationDatePatchBody(args.payload)),
      });
      writeExpirationDateToCache(updated.id, updated.expiration_date);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  const columns: DataTableColumn<DocumentListItemResource>[] = [
    { key: "original_filename" },
    {
      key: "category",
      render: (row) => (row.category ? (categoryLabelByValue[row.category] ?? row.category) : "—"),
    },
    {
      key: "entity_label",
      sortable: false,
      render: (row) =>
        row.entity_label && row.entity_url ? (
          <Link to={row.entity_url} className="text-ink underline" onClick={(event) => event.stopPropagation()}>
            {row.entity_label}
          </Link>
        ) : (
          "Unattached"
        ),
    },
    {
      key: "size_bytes",
      tabularFigures: true,
      align: "right",
      render: (row) => formatBytes(row.size_bytes),
    },
    {
      key: "uploaded_at",
      tabularFigures: true,
      render: (row) => formatDateCell(row.uploaded_at),
    },
    {
      // Story 7.3: editable right here on the shared list — a plain
      // `YYYY-MM-DD` text cell (this codebase's inline-edit table has no
      // dedicated "date" cell kind yet; every other date-shaped inline edit
      // elsewhere in this codebase uses the identical "text" kind).
      key: "expiration_date",
      tabularFigures: true,
      sortable: false,
      render: (row) => (row.expiration_date ? formatDateCell(row.expiration_date) : "—"),
      edit: { kind: "text" },
    },
    {
      // Story 15.2 (VC-15.2-05): resolved fresh on every read via
      // `replaced_by_label`/`replaced_by_url` — never a cached/stale label.
      // A plain `<a href>`, NOT a `Link` — unlike the `entity_label` column
      // immediately above (whose targets are real frontend record pages),
      // `replaced_by_url` always resolves to `/documents/{id}`, the real,
      // authenticated DOWNLOAD route (`app.domain.documents`'s own AD-27
      // registration — no document record page exists anywhere in this
      // codebase, that module's own doc comment). Mirrors the per-row
      // "Download" action's own identical plain-`<a>` treatment of this
      // exact URL shape, immediately to its left. "—" for a document that
      // has never been replaced, the same "no such state" dash every other
      // absent value on this table already uses.
      key: "replaced_by_document_id",
      sortable: false,
      render: (row) =>
        row.replaced_by_label && row.replaced_by_url ? (
          <>
            Superseded — replaced by{" "}
            <a
              href={row.replaced_by_url}
              className="text-ink underline"
              onClick={(event) => event.stopPropagation()}
            >
              {row.replaced_by_label}
            </a>
          </>
        ) : (
          "—"
        ),
    },
  ];

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <div className="mb-2">
        <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Documents</h1>
        <p className="mt-2 text-body text-ink-secondary">
          Every document in the vault. Add one from a property&apos;s own record page.
        </p>
      </div>
      <SavedViewChips listKey={DOCUMENTS_LIST_KEY} />
      <label className="mb-4 flex max-w-xs flex-col gap-1">
        <span className="text-label font-medium text-ink-secondary">Search by filename</span>
        <Input
          value={filenameTerm}
          onChange={(event) => setFilenameTerm(event.target.value)}
          placeholder="e.g. insurance"
        />
      </label>
      <DataTable
        caption="Documents"
        itemNamePlural="documents"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        filters={filters}
        {...(unfilteredTotalQuery.data ? { unfilteredTotal: unfilteredTotalQuery.data.meta.total } : {})}
        onCommitEdit={commitExpirationDateEdit}
        getRowHref={() => "/documents"}
        rowActions={(row) => (
          <div className="flex items-center gap-1">
            <a
              href={`/documents/${row.id}`}
              className="inline-flex h-tap-target-min items-center rounded-md px-3 text-button text-accent hover:bg-surface-hover"
            >
              Download
            </a>
            {/* Story 15.2 — a real "Replace" action next to Download
                (AC's own first bullet). Withheld for Read-only accounts
                (mirrors every other write affordance on this page) and for
                a row that has already been replaced once (VC-15.2-04: no
                re-replacing an already-superseded row from here — replacing
                the NEW document it links to is a separate, ordinary
                action on that row instead). */}
            {!isReadOnly && row.replaced_by_document_id == null ? (
              <Button
                type="button"
                variant="ghost"
                aria-label={`Replace ${row.original_filename}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setReplaceTarget(row);
                }}
              >
                Replace
              </Button>
            ) : null}
          </div>
        )}
        emptyState={{
          heading: "No documents yet.",
          body: "Documents will appear here once one is added from a property's own record page.",
        }}
      />
      {replaceTarget ? (
        <ReplaceDocumentDialog
          open={replaceTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setReplaceTarget(null);
            }
          }}
          oldDocument={replaceTarget}
          onReplaced={(result: DocumentReplacementResource) => {
            // Both the old row's own new "Superseded" label and the new
            // row itself need to show up — a broad invalidate of every
            // cached `["documents", ...]` query (mirrors `write
            // ExpirationDateToCache`'s own identical key-prefix scope) is
            // simpler and just as correct as hand-patching two rows that
            // may or may not both be on the currently-loaded page.
            void queryClient.invalidateQueries({ queryKey: ["documents"] });
            showToast(`Replaced with ${result.new_document.original_filename}.`);
          }}
        />
      ) : null}
    </div>
  );
}
